import type React from 'react';
import ReactECharts from 'echarts-for-react';
import {Box, Stack, Typography} from '@mui/material';
import type {SpendAnalysis} from '../../types/categoryProfileTypes';

type Props = {mix: SpendAnalysis['documentMix']};

export const DOCUMENT_MIX_SEGMENTS = [
    {key: 'withPo', label: 'With purchase order', color: '#7C5CFF'},
    {key: 'withoutPo', label: 'Without purchase order', color: '#2D6FF5'},
] as const;

/** Document mix (spec 4.4): lines with and without a purchase order as a donut with counts and shares. */
export const DocumentMixChart: React.FC<Props> = ({mix}) => {
    const rows = DOCUMENT_MIX_SEGMENTS.map(s => ({...s, value: mix[s.key]}));
    const total = rows.reduce((t, r) => t + r.value, 0);
    if (total === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{py: 6, textAlign: 'center'}}>No lines in this period.</Typography>;
    }
    const withPoPct = Math.round(100 * mix.withPo / total);
    return (
        <Stack direction={{xs: 'column', sm: 'row'}} alignItems="center" gap={2}>
            <Box sx={{position: 'relative', width: 200, height: 200, flexShrink: 0}}>
                <ReactECharts style={{width: 200, height: 200}} notMerge option={{
                    tooltip: {trigger: 'item', formatter: '{b}: {c} ({d}%)'},
                    series: [{type: 'pie', radius: ['62%', '88%'], avoidLabelOverlap: true, label: {show: false},
                        data: rows.map(r => ({name: r.label, value: r.value, itemStyle: {color: r.color}}))}],
                }}/>
                <Box sx={{position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none'}}>
                    <Box sx={{textAlign: 'center'}}>
                        <Typography variant="h5" sx={{fontWeight: 600, lineHeight: 1}}>{`${withPoPct}%`}</Typography>
                        <Typography variant="caption" color="text.secondary">with a PO</Typography>
                    </Box>
                </Box>
            </Box>
            <Stack gap={1} sx={{flex: 1, minWidth: 0, width: '100%'}}>
                {rows.map(r => (
                    <Stack key={r.key} direction="row" alignItems="center" gap={1}>
                        <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: r.color, flexShrink: 0}}/>
                        <Typography variant="body2" sx={{flex: 1}}>{r.label}</Typography>
                        <Typography variant="body2" sx={{fontWeight: 600}}>{r.value.toLocaleString()}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{width: 40, textAlign: 'right'}}>
                            {`${Math.round(100 * r.value / total)}%`}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};
